import {IDataChoices} from "../../model-factory/basic/data/DataChoice";
import {LocateDataModel} from "../locate/LocateDataFactory";
import {LngLat} from "maplibre-gl";
import DataModel from "../../model-factory/basic/data/DataModel";
import ApiRequestVO from "../../manager/api/ApiRequestVO";
import {IAnyObject} from "../../common/SimpleInterface";
import ApiError from "../../manager/api/ApiError";
import {IPopulateIDs} from "../../model-factory/type-interface/ModelFactoryInterface";
import {
    isArray,
    isNativeObject,
    multiNationalAlphabetic,
    requireDate
} from "../../common/SiteHelper";
import {PostStatus, PostType} from "../../manager/post/PostManager";
import {getModelFactory} from "../../model-factory/ModelFactory";
import {LocatePageListDataModel} from "../locate/LocatePageListDataFactory";
import {LocateCountryDataModel} from "../locate/LocateCountryDataFactory";
import {getManager} from "../../manager/Manager";
import FileVO from "../../common/FileVO";
import ImageVO from "../../common/ImageVO";
import MapLocationVO from "../../../tsx/component/form/model/vo/MapLocationVO";
import LocatableDataFactory, {LocatableDataChoice, LocatableDataModel} from "../locate/LocatableDataFactory";
import SimpleChoice from "../../common/SimpleChoiceVO";
import SocialChannelVO, {allSocialChannelsVOs} from "./SocialChannelVO";
import AddressVO from "./AddressVO";
import KeyContactVO from "./KeyContactVO";
import VideoVO from "./VideoVO";
import {FactoryID} from "../../model-factory/type-interface/ModelFactoryType";
import {
    applyConfigToPostAcfMeta,
    filterContentBlocks, getPostSubmitValues, getPostUIValues,
    getPostVisibleMeta,
    IContentBlock,
    parseContentBlocks
} from "./PostDataHelper";
import {NICounty} from "../../manager/GeneralManager";


export type OrganisationType =
    "vcse" |
    "supplier";

export class PostDataChoice extends LocatableDataChoice
{
    postID: number;
    postStatus:PostStatus

    organisationID:number = 0;
    acfMeta:IAnyObject;
    visibleMeta:IAnyObject = {};
    submitValues:IAnyObject = {};
    uiValues:IAnyObject = {};

    thumbnail: ImageVO|null = null

    description: string = "";
    mainContent: string = "";
    authorName: string = "";

    addressVO:AddressVO = new AddressVO();
    categories:SimpleChoice[] = [];
    categoriesListed:string = "";
    endsAt:Date|null = null;

    websiteAddress: string = "";
    emailAddress: string = "";
    telephoneNumber: string = "";
    mobileNumber: string = "";
    faxNumber: string = "";
    reference: string = "";

    socialChannelsVOs:SocialChannelVO[] = [];
    location:MapLocationVO|null = null;
    keyContactVOs:KeyContactVO[] = [];
    imageVOs:ImageVO[] = [];
    fileVOs:FileVO[] = [];
    videoVOs:VideoVO[] = [];

    organisationType: OrganisationType|null  = null;
    primaryChildPostType: PostType|null  = null;

    searchString:string = "";
    simpleSearchString:string = "";

    fromBlocksArray:any[] = [];
    contentBlocks:IContentBlock[] = [];

    constructor(object:IAnyObject)
    {

        const acfMeta:IAnyObject =  isNativeObject(object.acf_meta ?? null) ? object.acf_meta: {};

        const postID = object.id && !isNaN(object.id) ? parseInt(object.id) : 0;

        object.id = postID ? postID.toString() : object.id;
        object.name = acfMeta.title_acf ?? object.name;

        super(object);

        const postStatus = object.post_status as PostStatus;
        const postStatusName = getManager().post.postStatusNames[postStatus] ?? null;
        this.postStatus = postStatusName ? postStatus : "unknown"

        this.acfMeta = acfMeta;
        this.fromBlocksArray = isArray(object.blocks) ? object.blocks : [];
        this.postID = postID;
        this.organisationID = parseInt(acfMeta.organisation_id ?? "0");
        if(!this.isDataAppliedExternally()){
            this.applyAcfData();
            this.applyContentBlocks();
        }

    }

    isDataAppliedExternally():boolean
    {
        return false;
    }

    getPageID():string|null
    {
        return null;
    }

    applyAcfData():void
    {
        const object = this.receivedObject;

        const postType = object.post_type ?? null;
        if(getManager().post.postTypes.includes(postType)){
            this.postType = postType;
        }

        let overrideFieldNames:string[] = object.override_field_names ?? [];
        overrideFieldNames = isArray(overrideFieldNames) ? overrideFieldNames: [];

        applyConfigToPostAcfMeta(postType, this.acfMeta);
        this.submitValues = getPostSubmitValues(postType, this.acfMeta);
        this.visibleMeta = getPostVisibleMeta(postType, this.acfMeta, overrideFieldNames);
        this.uiValues = getPostUIValues(postType, this.visibleMeta);

        const visibleMeta = this.visibleMeta;

        const map = visibleMeta.map instanceof MapLocationVO ? visibleMeta.map : null;
        this.location = map;
        if(!!map && map.isValid()){
            this.lngLat = new LngLat(map.lng ?? 0, map.lat ?? 0);
            if(map.hasBounds()){
                this.lngLatBounds = getManager().location.determineBounds(map.lat ?? 0, map.lng ?? 0, map.boundsWidth ?? 0, map.boundsHeight ?? 0);
            }
        }
        const uiValues = this.uiValues;


        this.name = this.name ? this.name : (visibleMeta.title_acf ?? "");
        this.thumbnail = visibleMeta.image ? (visibleMeta.image[0] ?? null) : null;

        this.description = visibleMeta.description ?? "";
        this.mainContent = visibleMeta.content ?? "";
        this.authorName = object.author_name ?? "";


        const buildingName:string = visibleMeta.building_name ?? "";
        const address:string = visibleMeta.address ?? "";
        const town:string = visibleMeta.town ?? "";
        let county:string = visibleMeta.county ?? "";
        const niCounty = getManager().general.getNiCounty(county);
        county = niCounty !== "none" ? getManager().general.counties[niCounty].name : county;
        const postcode:string = visibleMeta.postcode ?? "";
        const countryCode:string = visibleMeta.country ?? "";
        const countryName:string = uiValues.country instanceof SimpleChoice ? uiValues.country.name : "";
        this.addressVO.title = this.name;
        this.addressVO.buildingName = buildingName;
        this.addressVO.address = address;
        this.addressVO.town = town;
        this.addressVO.county = county;
        this.addressVO.niCounty = niCounty;
        this.addressVO.postcode = postcode;
        this.addressVO.countryCode = countryCode;
        this.addressVO.countryName = countryName;

        const assignedCategories:any[] = uiValues.assigned_categories ?? [];
        if(Array.isArray(assignedCategories)){
            for(let category of assignedCategories){
                if(category instanceof SimpleChoice){
                    this.categories.push(category);
                    if(this.categoriesListed){
                        this.categoriesListed += ", "
                    }
                    this.categoriesListed += category.name
                }
            }
        }
        this.endsAt = visibleMeta.ends_at ? requireDate(visibleMeta.ends_at) : null;
        this.websiteAddress = visibleMeta.website_address ?? ""
        this.emailAddress = visibleMeta.email_address ?? ""
        this.telephoneNumber = visibleMeta.telephone_number ?? ""
        this.mobileNumber = visibleMeta.mobile_number ?? ""
        this.faxNumber = visibleMeta.fax_number ?? ""
        this.reference = visibleMeta.reference ?? ""

        const organisationType = visibleMeta.organisation_type ?? ""
        const organisationTypes:OrganisationType[] = ["vcse", "supplier"];
        this.organisationType = organisationTypes.includes(organisationType) ? organisationType : null;
        this.primaryChildPostType = this.organisationType === "vcse" ? "project" : (this.organisationType === "supplier" ? "offer" : null);

        const socialMedia = isNativeObject(visibleMeta.social_media) ? visibleMeta.social_media : {};
        for(let vo of allSocialChannelsVOs){
            const url = socialMedia[vo.nameKey] ?? null;
            if(!url){
                continue;
            }
            const clonedVO = vo.clone();
            clonedVO.url = url;
            this.socialChannelsVOs.push(clonedVO);

        }
        const fromKeyContacts:any[] = Array.isArray(visibleMeta.key_contacts) ? visibleMeta.key_contacts : [];
        for(let fromContact of fromKeyContacts){
            if(!isNativeObject(fromContact)){
                continue;
            }
            const name = fromContact.name;
            if(!!name){

                const role = fromContact.role;
                const emailAddress = fromContact.email_address;
                const telephoneNumber = fromContact.telephone_number;

                const keyContactVO = new KeyContactVO();
                keyContactVO.name = name;
                keyContactVO.role = role;
                keyContactVO.emailAddress = emailAddress;
                keyContactVO.telephoneNumber = telephoneNumber;
                this.keyContactVOs.push(keyContactVO);
            }
        }
        const fromImages:any[] = Array.isArray(visibleMeta.images) ? visibleMeta.images : [];
        for(let fromImage of fromImages){
            if(fromImage instanceof ImageVO){
                this.imageVOs.push(fromImage);
            }
        }
        const fromFiles:any[] = Array.isArray(visibleMeta.files) ? visibleMeta.files : [];
        for(let fromFile of fromFiles){
            if(fromFile instanceof FileVO){
                this.fileVOs.push(fromFile);
            } else if(isNativeObject(fromFile)){
                const fromFileFile:any[]|FileVO = fromFile.file;
                if(fromFileFile instanceof FileVO){
                    this.fileVOs.push(fromFileFile);
                } else if(Array.isArray(fromFileFile)){
                    for(let fromFileFileFile of fromFileFile){
                        if(fromFileFileFile instanceof FileVO){
                            this.fileVOs.push(fromFileFileFile);
                        }
                    }
                }
            }
        }
        const fromVideos:any[] = Array.isArray(visibleMeta.video_urls) ? visibleMeta.video_urls : [];
        for(let fromVideo of fromVideos){
            if(!isNativeObject(fromVideo)){
                continue;
            }
            let url:string|null = fromVideo.video_url ?? null
            const name = fromVideo.name ?? "";
            if(url){
                const parts = url.split("/");
                if(parts.length > 1 && !url.includes("youtube")){
                    continue;
                }
                let videoID = new URL(url).searchParams.get("v");
                if(!videoID){
                    videoID = parts[parts.length - 1];
                }
                url = "https://www.youtube.com/embed/" + videoID;

                this.videoVOs.push(new VideoVO(url, name));
            }
        }
        this.searchString = multiNationalAlphabetic(this.name + " " +this.addressVO.town + " " +this.addressVO.county + " " +this.categoriesListed, true, null, true).toLowerCase();
        this.simpleSearchString = multiNationalAlphabetic(this.name).toLowerCase();
    }

    getParentChoice():PostDataChoice|null {
        return (this.postType !== "organisation") && !!this.organisationID ? (getModelFactory().postData.getSharedDataModel("organisation").choices[this.organisationID] ?? null) : null;
    }

    applyContentBlocks():void
    {
        this.contentBlocks = parseContentBlocks(this.fromBlocksArray);
    }

    getContentBlocks():IContentBlock[]
    {
        return filterContentBlocks(this.contentBlocks);
    }
}

export interface IPostDataChoices extends IDataChoices
{
    [key: string]: PostDataChoice;
}


export class PostDataModel extends LocatableDataModel
{
    postID: number = 0;
    organisationID: number = 0;
    postType:PostType = null!;
    choices: IPostDataChoices = {};

    isEntryModel: boolean = false;
    isSharedData: boolean = false;

    mineOnly: boolean = false;
    includeMyDraft: boolean = false;
    includeMyPending: boolean = false;
    includeExpired: boolean = false;
    permitIfAdmin: boolean = false;
    limit = -1

    choiceList(choiceOrder?: string[]): PostDataChoice[]
    {
        return super.choiceList(choiceOrder) as PostDataChoice[];
    }

    applyModelConditions()
    {
        super.applyModelConditions();

        this.mineOnly = this.populateIDs.mine_only ?? false;
        this.postType = this.populateIDs.post_type ?? "organisation";
        this.postID = this.populateIDs.post_id ?? 0;
        this.organisationID = this.mineOnly && (this.populateIDs.post_type !== "organisation") ? getManager().user.organisationID : 0;
        this.isEntryModel = !!this.postID;
        this.isSharedData = this.populateIDs.is_shared_data ?? false;
        this.populated = this.isSharedData;

        if(this.isEntryModel || this.mineOnly){
            this.includeMyDraft = true;
            this.includeMyPending = true;
            this.includeExpired = true;
        }
        if(this.isEntryModel && !this.mineOnly){
            this.permitIfAdmin = true;
        }
    }
    async onPopulated(): Promise<void>
    {
        return super.onPopulated().then(() => {
            return this.requireOrganisations();
        });
    }

    async requireOrganisations(): Promise<void>
    {
        if((this.postType === "organisation") || (this.postType === "page")){
            return;
        }
        const organisationChoiceIDs:string[] = [];
        for(let choice of this.choiceList()){
            const organisationChoiceID = choice.organisationID ? choice.organisationID.toString() : "";
            if(organisationChoiceID && !organisationChoiceIDs.includes(organisationChoiceID)){
                organisationChoiceIDs.push(organisationChoiceID);
            }
        }
        if(!organisationChoiceIDs.length){
            return;
        }

        return getModelFactory().postData.getSharedDataModel("organisation").requestAdditionalChoices(organisationChoiceIDs).then()
    }

    getListEndpoint()
    {
        if(!this.organisationID){
            return "/post/" + this.postType +"/list";
        }
        return "/post/organisation/" + this.organisationID + "/" + this.postType + "/list";
    }

    getPopulateRequestVO(requestedIDs?:string[]): ApiRequestVO|null
    {
        const requestVO = new ApiRequestVO(true);
        requestVO.endpoint = this.getListEndpoint();
        const requestParams:IAnyObject = {};

        const postIDs:number[] = [];
        if(requestedIDs){
            for(let requestedID of requestedIDs)
            {
                const postID = parseInt(requestedID);
                if(postID){
                    postIDs.push(postID)
                }
            }
        } else if(this.isEntryModel){
            postIDs.push(this.postID)
        }

        if(postIDs.length){
            requestParams.post_id = postIDs.length > 1 ? postIDs : postIDs[0];
        } else {
            if(this.mineOnly){
                requestParams.mine_only = true;
            }
        }
        if(this.includeMyDraft){
            requestParams.include_my_draft = true;
        }
        if(this.includeMyPending){
            requestParams.include_my_pending = true;
        }
        if(this.includeExpired){
            requestParams.include_expired = true;
        }
        if(this.permitIfAdmin){
            requestParams.permit_if_admin = true;
        }
        if(this.limit > 0){
            requestParams.limit = this.limit;
        }

        requestVO.params = requestParams;
        return requestVO;
    }

    applyChoice(choice: PostDataChoice)
    {
        super.applyChoice(choice);
        choice.postType = this.postType;
        if(this.organisationID){
            choice.organisationID = this.organisationID;
        }
    }

    populateWithRequestResponse(response: IAnyObject, replaceData:boolean): ApiError | null
    {
        let error =  super.populateWithRequestResponse(response, replaceData);
        if(error){
            return error;
        }

        let entryObjects:IAnyObject[] = response.entries && Array.isArray(response.entries) ? response.entries : [];

        this.populateChoiceList(entryObjects, replaceData);
        return null;
    }

    populateRelatedModels(response:IAnyObject)
    {
        super.populateRelatedModels(response);
        const choiceList = this.choiceList();
        if(!this.isSharedData){
            this.factory().getSharedDataModel(this.postType).addToChoiceList(choiceList);
        }

        for(let i = 0; i < choiceList.length; i ++)
        {
            const choice = choiceList[i];
            if(choice.organisationID && choice.postType){
                getManager().post.postOrganisationIDs[choice.postID.toString()] = choice.organisationID
            }
        }
    }

    resetPopulated()
    {
        super.resetPopulated();
        if(this.isSharedData){
            this.populated = true;
        }
    }

    resetPopulatedAndRelated()
    {
        this.resetPopulated();
        const altModel = this.factory().getPostDataModel(this.postType, !this.mineOnly);
        altModel.resetPopulated();
        const sharedDataModel = this.factory().getSharedDataModel(this.postType);
        sharedDataModel.resetData();
    }

    factory(): PostDataFactory
    {
        return super.factory() as PostDataFactory;
    }
}

export default class PostDataFactory extends LocatableDataFactory
{

    postType:PostType = null!

    id: FactoryID = "post_data";

    birth(id: string): PostDataModel
    {
        return new PostDataModel(id)
    }

    birthDataChoice(object:IAnyObject): PostDataChoice
    {
        return new PostDataChoice(object);
    }

    getSharedDataModel(postType:PostType): PostDataModel
    {
        const populateIDs:IPopulateIDs = {
            is_shared_data: true,
            post_type: postType
        }
        return this.requireModel(populateIDs) as PostDataModel
    }

    getEntryDataModel(postType:PostType, postID:number): PostDataModel
    {
        const populateIDs:IPopulateIDs = {
            post_type: postType,
            post_id: postID
        }
        return this.requireModel(populateIDs) as PostDataModel
    }

    getPostDataModel(postType:PostType, mine:boolean = false): PostDataModel
    {
        const populateIDs:IPopulateIDs = {
            post_type: postType
        }

        if(mine){
            populateIDs.mine_only = true;
        }

        return this.requireModel(populateIDs) as PostDataModel
    }

    onPostStateChange(postID:number|null, postType:PostType, organisationID?:number)
    {

        getModelFactory().locateData.resetPopulated((model:DataModel|LocateDataModel) => {
            return (model as LocateDataModel).searchParams.postType === this.postType;
        });
        getModelFactory().locatePageListData.resetPopulated((model:DataModel|LocateDataModel) => {
            return (model as LocatePageListDataModel).searchParams.postType === this.postType;
        });
        getModelFactory().locateData.resetPopulated((model:DataModel|LocateDataModel) => {
            return (model as LocateCountryDataModel).searchParams.postType === this.postType;
        });

        for(let modelID in this.conditionModels){
            const model = this.conditionModels[modelID] as PostDataModel;
            if(!organisationID || (organisationID === model.organisationID)){
                model.resetPopulated();
            }
        }
        if(!organisationID){
            const sharedDataModel = this.getSharedDataModel(postType);

            sharedDataModel.resetData();
        }

    }
}